// React / Next
import { useState, useEffect, useCallback, useRef } from 'react';

// Types
import { SHOWCASE_SLIDES } from './types';

// Timer duration per slide in milliseconds (10 seconds)
export const SLIDE_DURATION = 10000;

type ShowcaseTimerState = {
  slideIndex: number;
  progress: number; // 0-100 progress within current slide
  isPlaying: boolean;
};

type UseShowcaseTimerOptions = {
  isInView?: boolean; // Only start animation when in view
};

type UseShowcaseTimerReturn = {
  state: ShowcaseTimerState;
  goToSlide: (index: number) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  pause: () => void;
  play: () => void;
};

/**
 * Custom hook for managing showcase timer with auto-advance
 * Cycles through all slides, then loops back
 */
export const useShowcaseTimer = (
  options: UseShowcaseTimerOptions = {}
): UseShowcaseTimerReturn => {
  const { isInView = true } = options;
  const totalSlides = SHOWCASE_SLIDES.length;

  const [state, setState] = useState<ShowcaseTimerState>({
    slideIndex: 0,
    progress: 0,
    isPlaying: false, // Start paused, will play when in view
  });

  // Track if we've ever been in view (to trigger initial animation)
  const hasBeenInViewRef = useRef(false);
  const startTimeRef = useRef<number>(Date.now());
  const animationFrameRef = useRef<number>();

  // Animation loop for smooth progress
  const tick = useCallback(() => {
    if (!state.isPlaying) return;

    const elapsed = Date.now() - startTimeRef.current;
    const progress = Math.min((elapsed / SLIDE_DURATION) * 100, 100);

    if (progress >= 100) {
      // Move to next slide
      const nextIndex = (state.slideIndex + 1) % totalSlides;
      startTimeRef.current = Date.now();
      setState((prev) => ({
        ...prev,
        slideIndex: nextIndex,
        progress: 0,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        progress,
      }));
    }

    animationFrameRef.current = requestAnimationFrame(tick);
  }, [state.isPlaying, state.slideIndex, totalSlides]);

  // Start playing when first coming into view
  useEffect(() => {
    if (isInView && !hasBeenInViewRef.current) {
      hasBeenInViewRef.current = true;
      startTimeRef.current = Date.now();
      setState((prev) => ({ ...prev, isPlaying: true }));
    }
  }, [isInView]);

  // Start/stop animation loop
  useEffect(() => {
    if (state.isPlaying && isInView) {
      animationFrameRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [state.isPlaying, isInView, tick]);

  // Go to specific slide
  const goToSlide = useCallback((index: number) => {
    startTimeRef.current = Date.now();
    setState((prev) => ({
      ...prev,
      slideIndex: index,
      progress: 0,
    }));
  }, []);

  // Go to previous slide
  const goToPrevious = useCallback(() => {
    const prevIndex = state.slideIndex === 0 ? totalSlides - 1 : state.slideIndex - 1;
    startTimeRef.current = Date.now();
    setState((prev) => ({
      ...prev,
      slideIndex: prevIndex,
      progress: 0,
    }));
  }, [state.slideIndex, totalSlides]);

  // Go to next slide
  const goToNext = useCallback(() => {
    const nextIndex = (state.slideIndex + 1) % totalSlides;
    startTimeRef.current = Date.now();
    setState((prev) => ({
      ...prev,
      slideIndex: nextIndex,
      progress: 0,
    }));
  }, [state.slideIndex, totalSlides]);

  // Pause timer
  const pause = useCallback(() => {
    setState((prev) => ({ ...prev, isPlaying: false }));
  }, []);

  // Resume timer
  const play = useCallback(() => {
    startTimeRef.current = Date.now() - (state.progress / 100) * SLIDE_DURATION;
    setState((prev) => ({ ...prev, isPlaying: true }));
  }, [state.progress]);

  return {
    state,
    goToSlide,
    goToPrevious,
    goToNext,
    pause,
    play,
  };
};
